import moment from 'moment';
import { PICK_ARRAYS } from '../../containers/ResidentProfile/constants';
import {
  formatCalendarDateUtc,
  formatDateLocale,
} from '../../utils/date-helpers';

// Ports ResidentProfile/selectors.js's getHouseholdResidentMembers (and its
// parseGeneralInfo/parsePersonalInfo/parseAffordableInfo/parseResidentInfo
// helpers) to a pure, Redux-free function so it can run against React Query
// data instead of `state.editResidentHousehold`/`state.residentProfile`.
// Keep this in lockstep with that selector until it's retired.

const pickFields = (keys, obj = {}) =>
  keys.reduce((picked, key) => {
    if (key in obj) picked[key] = obj[key];
    return picked;
  }, {});

const normalizeLegacyEthnicity = (ethnicity) => {
  if (ethnicity === 'notWishToFurnish') {
    return 'notWishToProvide';
  }
  return ethnicity;
};

// The four sub-answers are BOOLEAN columns while hasDisability is an enum
// string; the selects are keyed on 'yes'/'no', so a raw `false` matched none.
const DISABILITY_SUB_FIELDS = [
  'disabilityMobile',
  'disabilityHearing',
  'disabilityVisual',
  'disabilityOther',
];

const normalizeDisability = (disability) => {
  if (!disability) return null;
  const normalized = { ...disability };
  DISABILITY_SUB_FIELDS.forEach((field) => {
    const value = normalized[field];
    // null stays blank -- it is the stored state for "do not wish to provide".
    if (typeof value === 'boolean') {
      normalized[field] = value ? 'yes' : 'no';
    } else if (value == null) {
      normalized[field] = '';
    }
  });
  return normalized;
};

const sanitizeMultiRace = (multiRace, race) => {
  const sanitized = Array.isArray(multiRace) ? multiRace.filter(Boolean) : [];
  if (sanitized.length > 0) {
    return sanitized;
  }
  return race ? [race] : null;
};

export const parseGeneralInfo = (
  customer,
  type,
  relationshipId,
  applicantType,
  isPrimary = false,
) => {
  const suffixId = customer?.suffixId ?? '';
  const generalInformation = pickFields(
    PICK_ARRAYS[type].generalInformation,
    customer,
  );
  const adultFields =
    type !== 'adult'
      ? {}
      : {
          applicantTypeId: applicantType?.id || 'default',
          relationshipId: isPrimary ? 'default' : relationshipId,
        };
  // Minors have no Type/applicantTypeId concept (adult-only), but do have
  // their own Relation, sourced the same way (Applicant-side relationshipId).
  // Can still be 'default' here too -- not from the isPrimary branch above
  // (a minor is never primary/HoH), but because callers of this function
  // already default a missing relationshipId to 'default' before it arrives.
  const minorFields = type !== 'minor' ? {} : { relationshipId };
  return {
    ...generalInformation,
    ...(generalInformation.preferredName && {
      preferredName: generalInformation.preferredName.trim(),
    }),
    ...(generalInformation.middleName && {
      middleName: generalInformation.middleName.trim(),
    }),
    suffixId,
    ...adultFields,
    ...minorFields,
  };
};

export const parsePersonalInfo = (applicantInfo, customer, type) => {
  const basePersonalInformation = pickFields(
    PICK_ARRAYS[type].personalInformation,
    customer,
  );
  if (type !== 'adult') {
    const dateOfBirth = formatCalendarDateUtc(
      basePersonalInformation.dateOfBirth,
    );
    return { ...basePersonalInformation, dateOfBirth };
  }
  const birthday = formatCalendarDateUtc(basePersonalInformation.birthday);
  const maritalStatus = applicantInfo?.applicantCustomer?.maritalStatus ?? '';
  // Applicant column, not customer, so pickFields never sees it; null (not '')
  // so getDefaultValues can still fall through to the separately fetched value.
  const annualIncome = applicantInfo?.annualIncome ?? null;
  return {
    ...basePersonalInformation,
    maritalStatus,
    birthday,
    annualIncome,
  };
};

export const parseAffordableInfo = (applicantInfo, customer, type) => {
  const affordableRelationshipId =
    applicantInfo?.affordableRelationshipId ?? 'default';
  const affordableInformation = pickFields(
    PICK_ARRAYS[type].affordableInformation,
    customer,
  );
  const disability = normalizeDisability(customer?.customerDisability);
  // Written to the Applicant record (ApplicantMinors.js#updateMinor/
  // Residents.js#updateResident), same as affordableRelationshipId above —
  // not a Customer/ApplicantMinor column, so pickFields(..., customer)
  // above can never see it. Missing here meant Save worked but the
  // Mini-Profile always displayed it back as empty on reload.
  const specialNeedsDesignationIds =
    applicantInfo?.specialNeedsDesignationIds ?? [];

  return {
    ...affordableInformation,
    multiRace: sanitizeMultiRace(
      affordableInformation.multiRace,
      affordableInformation.race,
    ),
    ethnicity: normalizeLegacyEthnicity(affordableInformation.ethnicity),
    disability,
    affordableRelationshipId,
    specialNeedsDesignationIds,
  };
};

const getByPath = (obj, path) => path.reduce((value, key) => value?.[key], obj);

// PortalUser.lastSystemAccess/claimRequestedAt are real timestamptz values,
// unlike the DATEONLY-semantics columns formatCalendarDateUtc exists for —
// render them in the viewer's local zone, with the time, per the design.
const PORTAL_TIMESTAMP_FORMAT = 'MM/DD/YYYY hh:mm A';

const formatPortalTimestamp = (value) =>
  value && moment(value).isValid()
    ? formatDateLocale(value, PORTAL_TIMESTAMP_FORMAT)
    : '';

// Seeded on Customer rows that never had a real address; the legacy Portal
// panel hides it rather than showing it as an email.
const PLACEHOLDER_EMAIL_ADDRESS = 'batch_default@mail.com';

// PortalUser (src/models/portalUser.js) — isClaimed/lastSystemAccess/
// claimRequestedAt are the real columns; the Mini-Profile's Portal Access
// section previously never read any of this and just showed blank fields.
// Exported for the applicant path, which builds this from the portal status endpoint.
export const parsePortalAccessInfo = (portalUser = {}, customer = {}) => ({
  emailAddress:
    customer.emailAddress && customer.emailAddress !== PLACEHOLDER_EMAIL_ADDRESS
      ? customer.emailAddress
      : '',
  claimed:
    portalUser.isClaimed === true
      ? 'yes'
      : portalUser.isClaimed === false
      ? 'no'
      : '',
  lastLogin: formatPortalTimestamp(portalUser.lastSystemAccess),
  inviteSent: formatPortalTimestamp(portalUser.claimRequestedAt),
});

// applicantInfo is the Applicant-side cross-reference — it's missing
// whenever the Applicant has been soft-deleted, which happens for any Prior
// (removed) household member, adult or minor (see getApplicantsModels's
// `where: { deletedAt: null }`). Since Prior members must remain fully
// accessible/editable, fall back to the Resident-side
// data the household record always carries: fallbackCustomer (adult's
// rc.customer / minor's residentMinor) for General Information display, and
// residentId (the member's own Resident.id, always present) for the ids
// the Save payload needs when there's no Applicant to cross-reference.
export const parseResidentInfo = (
  applicantInfo,
  customerPath,
  type,
  isPrimary,
  relationshipId = '',
  address = {},
  fallbackCustomer = {},
  residentId = '',
) => {
  const customer = getByPath(applicantInfo, customerPath) ?? fallbackCustomer;
  const applicantType = applicantInfo?.applicantType ?? {};
  const generalInformation = parseGeneralInfo(
    customer,
    type,
    relationshipId,
    applicantType,
    isPrimary,
  );
  const personalInformation = parsePersonalInfo(applicantInfo, customer, type);
  const affordableInformation = parseAffordableInfo(
    applicantInfo,
    customer,
    type,
  );
  let id;
  let minorsResId;
  if (type === 'adult') {
    id = applicantInfo?.resident?.id || residentId || '';
  } else if (type === 'minor') {
    id = applicantInfo?.applicantMinor?.id || residentId || '';
    minorsResId = applicantInfo?.resident?.id || residentId || '';
  }
  const adultObj = {
    id,
    minorsResId,
    type,
    generalInformation: { ...generalInformation, ...address },
    personalInformation,
    affordableInformation,
  };
  return type === 'adult' ? adultObj : { ...adultObj, minorsResId };
};

/**
 * Combines a household's raw member records (adults/minors/pets from
 * ResidentService.getResidentHousehold) with the resident's applicant array
 * (from ResidentService.getResident's `applicantInfo.applicants`) into the
 * same shape ResidentProfile/selectors.js's getHouseholdResidentMembers
 * produces today. Pure and Redux-free — safe to call directly from a React
 * Query hook.
 */
export const buildHouseholdMembers = ({
  household = [],
  applicants = [],
  referralType = '',
}) => {
  const currentResidents = household ?? [];
  const adultMembers = currentResidents
    .filter((a) => a?.rc != null)
    .map((adult) => {
      const adultApplicantId = adult?.applicant?.id ?? adult?.applicantId ?? '';
      const applicantInfo = applicants.find(
        (applicant) => applicant.id === adultApplicantId,
      );
      const financiallyResponsible =
        applicantInfo?.applicantType?.financiallyResponsible ?? false;
      const plAccount =
        applicantInfo?.applicantCustomer?.customer?.plAccount?.customerId ??
        null;
      // applicantInfo is absent for any Prior member (soft-deleted Applicant,
      // see parseResidentInfo's comment). The Portal status lookup still
      // needs a Customer id, and rc.customer is always present.
      const customerId =
        applicantInfo?.applicantCustomer?.customer?.id ??
        adult?.rc?.customer?.id ??
        null;
      const isPrimary = adult?.rc?.isPrimary ?? false;
      const relationshipId = adult?.relationshipId ?? 'default';
      const address = applicantInfo?.applicantCustomer?.address ?? null;
      const residentInfo = parseResidentInfo(
        applicantInfo,
        ['applicantCustomer', 'customer'],
        'adult',
        isPrimary,
        relationshipId,
        address,
        adult.rc.customer,
        adult.id,
      );
      const rla = applicantInfo?.rla || {};

      const portalUser = adult?.rc?.customer?.portalUser ?? {};
      residentInfo.portalAccess = parsePortalAccessInfo(
        portalUser,
        adult?.rc?.customer ?? {},
      );
      const primaryForm = applicantInfo?.primaryForm ?? null;
      const secondaryForm = applicantInfo?.secondaryForm ?? null;
      const shortForm = applicantInfo?.shortForm ?? null;
      const emailAddress =
        applicantInfo?.applicantCustomer?.customer?.emailAddress ?? null;

      let formData;
      if (primaryForm) {
        formData = { ...primaryForm, type: 'primary' };
      } else if (secondaryForm) {
        formData = { ...secondaryForm, type: 'secondary' };
      } else if (shortForm) {
        formData = { ...shortForm, type: 'short' };
      } else {
        formData = {};
      }
      return {
        id: adult.id,
        name: [
          adult.rc.customer.firstName,
          adult.rc.customer.lastName,
          adult.rc.customer.suffix ? adult.rc.customer.suffix.name : '',
        ].join(' '),
        relationship: adult.relationship ? adult.relationship.name : '---',
        relationshipId: adult?.relationship?.id,
        status: adult.status,
        // Design gap (ENG-30373 Row 5/6 Col 2) -- Phone/Email Type.
        // Residents.js#getResidentHousehold already attaches this
        // (ENG-12772), including the primary entry with its type; this
        // mapper just never surfaced it to useMiniProfileSections.js.
        contactInformation: adult.contactInformation,
        portalUser,
        // Resident.startDate/endDate are a full timestamptz column (not
        // DATEONLY like lease.startDate already is), so the backend
        // returns a UTC-midnight ISO string — passing that straight to the
        // date field lets it get re-interpreted in the browser's local
        // timezone and displays a day early for any negative UTC offset.
        // Same fix already applied to dateOfBirth/birthday below.
        startDate: formatCalendarDateUtc(adult.startDate),
        endDate: formatCalendarDateUtc(adult.endDate),
        applicantType: applicantInfo?.applicantType?.name ?? '',
        applicantTypeId: applicantInfo?.applicantType?.id ?? '',
        // resident.schema.js's applicantInformation.applicantId is
        // Joi.string().guid() — an empty string fails that format check.
        // When the real Applicant is unreachable (Prior member,
        // soft-deleted), fall back to the Resident's own id: it won't
        // match a real Applicant row; only avoids a schema 400.
        // buildSavePayload drops it (FK on Resident.applicantId).
        applicantId: applicantInfo?.id ?? adult?.applicant?.id ?? adult.id,
        type: 'adult',
        referralType,
        formData,
        residentInfo,
        financiallyResponsible,
        plAccount,
        customerId,
        emailAddress,
        isPrimary,
        rla,
        firstName: adult.rc.customer.firstName,
        preferredName: adult.rc.customer.preferredName
          ? adult.rc.customer.preferredName
          : '---',
        parentCompany: adult?.rc?.customer?.parentCompany ?? '---',
        // ENG-30290 -- Residents.js#getResidentHousehold (backend) already
        // attaches `{ emails: [...], phones: [...] }` (ContactEmail/
        // ContactPhone rows, including the primary) onto every adult record
        // here; this was previously read and dropped on the floor.
        // useMiniProfileSections.js's getDefaultValues reads this to
        // populate the Secondary/Tertiary Phone/Email fields, and also
        // (design gap, ENG-30373 Row 5/6 Col 2) the primary's own Phone
        // Type/Email Type.
        contactInformation: adult.contactInformation,
      };
    });

  const minorMembers = currentResidents
    .filter((a) => a?.residentMinor != null)
    .filter((minor) => {
      // Aged-up minors keep their old minor household-member record; exclude
      // it once they also show up as an adult (matched via rla.applicantIdMinor).
      const minorId = minor?.applicantId;
      const isAgedUpMinor = adultMembers.some((adult) => {
        const applicantIdMinor = adult?.rla?.applicantIdMinor;
        return applicantIdMinor === minorId;
      });
      return !isAgedUpMinor;
    })
    .map((minor) => {
      const minorApplicantId = minor?.applicant?.id ?? minor?.applicantId ?? '';
      const applicantInfo = applicants.find(
        (applicant) => applicant.id === minorApplicantId,
      );
      const relationshipId = minor?.relationshipId ?? 'default';
      const residentInfo = parseResidentInfo(
        applicantInfo,
        ['applicantMinor'],
        'minor',
        false,
        relationshipId,
        {},
        minor.residentMinor,
        minor.id,
      );
      return {
        id: minor.id,
        name: [
          minor.residentMinor.firstName,
          minor.residentMinor.lastName,
          minor.residentMinor.suffix ? minor.residentMinor.suffix.name : '',
        ].join(' '),
        relationship: minor.relationship ? minor.relationship.name : 'Minor',
        relationshipId: minor?.relationship?.id,
        // ApplicantMinors.js's updateMinor Joi schema requires applicantId
        // as a non-empty string — when the real Applicant is unreachable
        // (Prior member, soft-deleted), fall back to the Resident's own id.
        // It won't match any real Applicant row, so applicantsRepository's
        // update is a harmless no-op — this only avoids the 400 that would
        // otherwise reject the request before it ever reaches the handler.
        minorApplicantId: applicantInfo?.id || minorApplicantId || minor.id,
        status: minor.status,
        // See the adult mapping above for why these need UTC-anchored
        // parsing instead of a raw pass-through.
        startDate: formatCalendarDateUtc(minor.startDate),
        endDate: formatCalendarDateUtc(minor.endDate),
        type: 'minor',
        residentInfo,
      };
    });

  const petMembers = currentResidents
    .filter((a) => a?.residentPet != null)
    .map((pet) => ({
      id: pet.id,
      name: `${pet.residentPet.name}`,
      relationship: pet.residentPet.petType
        ? pet.residentPet.petType.name
        : 'Pet',
      isServiceAnimal: pet.residentPet?.isServiceAnimal,
      status: pet.status,
      startDate: pet.startDate,
      endDate: pet.endDate,
      type: 'pet',
    }));

  return [...adultMembers, ...minorMembers, ...petMembers];
};
